import { fileTypeFromBlob, fileTypeFromBuffer } from 'file-type';

import { getImageTypeByUrl } from '@/utils/photos';
import { getVideoTypeByURL } from '@/utils/videos';
import { request } from '@/utils/api/request';

import { ENDPOINT, HEADERS } from '@/constants/api';
import { MEDIA_TYPE } from '@/constants/studio';
import { IMAGE_TYPE } from '@/constants/photos';

export const studio = {
  saveFile({ db, file, folder, shopId, signal, type }) {
    switch (type) {
      case MEDIA_TYPE.PHOTOS: {
        return request.post({
          endpoint: ENDPOINT.WEB_V2,
          params: { db },
          payload: {
            alt_text: file.altText,
            fileName: file.fileName,
            folderUuid: folder || undefined,
            format: file.format,
            is_layer: !!file.layer,
            is_studio_background: !!file.background,
            url: file.url,
          },
          signal,
          url: `/shops/${shopId}/mediaLibrary/files`,
        })
          .then(function onSaved({ fileUuid: uuid }) {
            return Promise.resolve({ uuid });
          });
      }

      case MEDIA_TYPE.VIDEOS: {
        return request.post({
          endpoint: ENDPOINT.WEB_V2,
          params: { db },
          payload: {
            file_name: file.fileName,
            folder_uuid: folder || undefined,
            format: file.format,
            thumbnail_url: file.thumbnailUrl,
            video_url: file.url,
          },
          signal,
          url: `/shops/${shopId}/videoLibrary/files`,
        });
      }

      default: {
        return Promise.reject('Unknown type');
      }
    }
  },
  uploadFile({ db, file, shopId, signal, type }) {
    switch (type) {
      case MEDIA_TYPE.PHOTOS: {
        const { mime } = file;
        return request.get({
          endpoint: ENDPOINT.WEB_V2,
          params: { db, mime },
          signal,
          url: `/shops/${shopId}/mediaLibrary/files/uploadUrl`,
        }).then(function onUploadURL({ uploadUrl: url }) {
          const { data } = file;
          return request.put({
            headers: { [HEADERS.CONTENT_TYPE]: mime, [HEADERS.EXPIRES]: 0 },
            payload: data,
            signal,
            url,
          })
            .then(function onUploaded() {
              const { protocol, pathname } = new URL(url);
              return Promise.resolve({ url: `${protocol}/${pathname}` });
            });
        });
      }

      case MEDIA_TYPE.VIDEOS: {
        const { format, thumbnailFormat } = file;
        return request.get({
          endpoint: ENDPOINT.WEB_V2,
          params: { db, thumbnail_format: thumbnailFormat, video_format: format },
          signal,
          url: `/shops/${shopId}/videoLibrary/files/uploadUrl`,
        }).then(function onUploadURLs({
          thumbnail_download_url: thumbnailDownloadUrl,
          thumbnail_upload_url: thumbnailUploadUrl,
          video_download_url: videoDownloadUrl,
          video_upload_url: videoUploadUrl,
        }) {
          const { data, mime, thumbnailData, thumbnailMime } = file;

          return Promise.all([
            request.put({
              headers: { [HEADERS.CONTENT_TYPE]: mime, [HEADERS.EXPIRES]: 0 },
              payload: data,
              signal,
              url: videoUploadUrl,
            }),
            request.put({
              headers: { [HEADERS.CONTENT_TYPE]: thumbnailMime, [HEADERS.EXPIRES]: 0 },
              payload: thumbnailData,
              signal,
              url: thumbnailUploadUrl,
            }),
          ])
            .then(function onUpload() {
              return Promise.resolve({ thumbnailUrl: thumbnailDownloadUrl, url: videoDownloadUrl });
            });
        });
      }

      default: {
        return Promise.reject('Unknown type');
      }
    }
  },
  photos: {
    createFolder({ db, name, shopId }) {
      return request.post({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: { folderName: name },
        url: `/shops/${shopId}/mediaLibrary/folders/`,
      });
    },
    deleteFolder({ db, folder, shopId }) {
      return request.delete({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        url: `/shops/${shopId}/mediaLibrary/folders/${folder}`,
      });
    },
    deleteImages({ db, shopId, uuids }) {
      return request.delete({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: { uuids },
        url: `/shops/${shopId}/mediaLibrary/files`,
      });
    },
    downloadImage({ blob, data, signal, url }) {
      if (blob instanceof Blob) {
        return fileTypeFromBlob(blob)
          .then(function onFileType({ ext, mime }) {
            if (signal && signal.aborted) return;

            return { blob, format: ext, mime };
          });
      }

      if (data instanceof ArrayBuffer) {
        return fileTypeFromBuffer(data)
          .then(function onFileType({ ext, mime }) {
            if (signal && signal.aborted) return;

            return { blob: new Blob([data]), format: ext, mime };
          });
      }

      return new Promise(function executor(resolve, reject) {
        const type = getImageTypeByUrl(url);

        if (type === null) reject('Unknown image type');

        const options = {
          credentials: type === IMAGE_TYPE.ETSY,
          signal,
          url,
        };

        if (options.credentials) {
          options.params = { url };
          options.url = '/getImage64';
        }

        request
          .get(options)
          .then(
            function onImageDownloaded(imageBlob) {
              if (signal && signal.aborted) return;

              fileTypeFromBlob(imageBlob)
                .then(
                  function onFileType({ ext, mime }) {
                    resolve({ blob: imageBlob, format: ext, mime });
                  },
                  reject,
                );
            },
            reject,
          );
      });
    },
    downloadImages({ db, shopId, uuids }) {
      if (typeof uuids === 'string') {
        return request.get({
          endpoint: ENDPOINT.WEB_V2,
          params: { db },
          url: `/shops/${shopId}/mediaLibrary/download/${uuids}`,
        });
      } else if (Array.isArray(uuids)) {
        return request.post({
          endpoint: ENDPOINT.WEB_V2,
          params: { db },
          payload: { uuids: uuids },
          url: `/shops/${shopId}/mediaLibrary/bulkDownload`,
        });
      } else {
        return Promise.reject();
      }
    },
    getFolders({ db, shopId, signal }) {
      return request.get({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        signal,
        url: `/shops/${shopId}/mediaLibrary/folders`,
      });
    },
    getItems({ db, filters, folder, shopId, signal }) {
      const params = { ...filters, db };

      return request.get({
        endpoint: ENDPOINT.WEB_V2,
        params,
        signal,
        url: folder
          ? `/shops/${shopId}/mediaLibrary/folders/${folder}/images`
          : `/shops/${shopId}/mediaLibrary/images`,
      });
    },
    getListings({ db, groupId, limit, offset, shopId, signal }) {
      return request.get({
        endpoint: ENDPOINT.WEB_V2,
        params: { db, limit, offset },
        signal,
        url: `/shops/${shopId}/mediaLibrary/images/group/${groupId}`,
      });
    },
    moveImages({ db, shopId, source: sourceFolderUuid, target: destinationFolderUuid, uuids }) {
      return request.put({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: {
          uuids,
          destinationFolderUuid,
          sourceFolderUuid,
        },
        url: `/shops/${shopId}/mediaLibrary/files/move`,
      });
    },
    renameFolder({ db, folder, name, shopId }) {
      return request.put({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: { name },
        url: `/shops/${shopId}/mediaLibrary/folders/${folder}`,
      });
    },
    updatePhoto({ altText, db, folder, format, name, shopId, url, uuid }) {
      return request.put({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: { alt_text: altText, fileName: name, folderUuid: folder || null, format, url },
        url: `/shops/${shopId}/mediaLibrary/files/${uuid}`,
      });
    },
    updatePhotoAltText({ altText, db, shopId, uuid }) {
      return request.put({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: { alt_text: altText },
        url: `/shops/${shopId}/mediaLibrary/files/${uuid}`,
      });
    },
    smartRemove({ image, mask }) {
      const payload = new FormData();
      payload.append('image_file', image);
      payload.append('mask_file', mask);

      return request.post({
        endpoint: ENDPOINT.WEB_V2,
        payload,
        url: '/photoEditor/smart-remove',
      });
    },
  },
  videos: {
    getFolders({ db, shopId, signal }) {
      return request.get({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        signal,
        url: `/shops/${shopId}/videoLibrary/folders`,
      });
    },
    createFolder({ db, name, shopId }) {
      return request.post({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: { folderName: name },
        url: `/shops/${shopId}/videoLibrary/folders/`,
      });
    },
    renameFolder({ db, folder, name, shopId }) {
      return request.put({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: { name },
        url: `/shops/${shopId}/videoLibrary/folders/${folder}`,
      });
    },
    deleteFolder({ db, folder, shopId }) {
      return request.delete({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        url: `/shops/${shopId}/videoLibrary/folders/${folder}`,
      });
    },
    getItems({ db, filters, folder, shopId, signal }) {
      const params = { ...filters, db };

      return request.get({
        endpoint: ENDPOINT.WEB_V2,
        params,
        signal,
        url: folder
          ? `/shops/${shopId}/videoLibrary/folders/${folder}/videos`
          : `/shops/${shopId}/videoLibrary/videos`,
      });
    },
    getListings({ db, groupId, limit, offset, shopId, signal }) {
      return request.get({
        endpoint: ENDPOINT.WEB_V2,
        params: { db, limit, offset },
        signal,
        url: `/shops/${shopId}/videoLibrary/videos/group/${groupId}`,
      });
    },
    deleteVideos({ db, shopId, uuids }) {
      return request.delete({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: { uuids },
        url: `/shops/${shopId}/videoLibrary/files`,
      });
    },
    downloadVideo({ signal, url }) {
      return new Promise(function executor(resolve, reject) {
        const type = getVideoTypeByURL(url);

        if (type === null) reject('Unknown video type');

        const options = {
          credentials: false,
          signal,
          url,
        };

        request
          .get(options)
          .then(
            function onImageDownloaded(blob) {
              if (signal && signal.aborted) return;

              fileTypeFromBlob(blob)
                .then(
                  function onFileType({ ext, mime }) {
                    resolve({ blob, format: ext, mime });
                  },
                  reject,
                );
            },
            reject,
          );
      });
    },
    downloadVideos({ db, shopId, uuids }) {
      if (typeof uuids === 'string') {
        return request.get({
          endpoint: ENDPOINT.WEB_V2,
          params: { db },
          url: `/shops/${shopId}/videoLibrary/download/${uuids}`,
        });
      } else if (Array.isArray(uuids)) {
        return request.post({
          endpoint: ENDPOINT.WEB_V2,
          params: { db },
          payload: { uuids: uuids },
          url: `/shops/${shopId}/videoLibrary/bulkDownload`,
        });
      } else {
        return Promise.reject();
      }
    },
    moveVideos({ db, shopId, source: sourceFolderUuid, target: destinationFolderUuid, uuids }) {
      return request.put({
        endpoint: ENDPOINT.WEB_V2,
        params: { db },
        payload: {
          uuids,
          destinationFolderUuid,
          sourceFolderUuid,
        },
        url: `/shops/${shopId}/videoLibrary/files/move`,
      });
    },
  },
};
